import { useEffect, useState } from 'react';
import { whatsappAPI, timesheetAPI, billingAPI, systemSettingsAPI, assetAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { Loader2, MessageCircle, Mail, CreditCard, Building2, Shield, Globe, Trash2, AlertTriangle, X, Plus, Download, Upload, Database, RefreshCw, CheckCircle2, Users, Package } from 'lucide-react';

export default function SettingsPage() {
  const { isAdmin, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    phone_number_id: '',
    business_account_id: '',
    access_token: '',
    webhook_verify_token: '',
    enabled: false,
  });
  
  // Email settings
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'Vibe6 Intranet',
    use_tls: true,
    enabled: false,
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Razorpay settings (Super Admin only)
  const [razorpayConfig, setRazorpayConfig] = useState({
    enabled: false,
    key_id: '',
    key_secret: '',
    webhook_secret: '',
    test_mode: true,
  });
  const [savingRazorpay, setSavingRazorpay] = useState(false);
  
  // Bank Details settings (Super Admin only)
  const [bankConfig, setBankConfig] = useState({
    india: {
      company_name: 'VIBE6 DIGITAL LLP',
      bank_name: 'IDFC FIRST Bank',
      branch: 'INDORE – VIJAY NAGAR BRANCH',
      account_number: '',
      ifsc: '',
      swift_code: '',
      upi_id: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      gstin: '',
    },
    usa: {
      company_name: 'VIBE6 DIGITAL LLC',
      bank_name: 'Chase Bank',
      zelle_email: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      ein: '',
    }
  });
  const [savingBank, setSavingBank] = useState(false);

  // Organization/Domain settings (Super Admin only)
  const [orgConfig, setOrgConfig] = useState({
    allowed_domains: ['vibe6.com'],
    enable_alerts_for_external: false,
    sync_external_employees: false,
    newDomain: ''
  });
  const [savingOrg, setSavingOrg] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);

  // Backup & Restore state
  const [backupStatus, setBackupStatus] = useState(null);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);

  // Daily Digest state
  const [digestPreview, setDigestPreview] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  // Document Expiry state
  const [expiryData, setExpiryData] = useState(null);
  const [loadingExpiry, setLoadingExpiry] = useState(false);
  const [sendingExpiryAlert, setSendingExpiryAlert] = useState(false);

  // Asset Allocation Reminders state
  const [allocationData, setAllocationData] = useState(null);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [sendingAllocationReminders, setSendingAllocationReminders] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [waRes, emailRes] = await Promise.all([
          whatsappAPI.getConfig().catch(() => ({ data: null })),
          timesheetAPI.getEmailSettings().catch(() => ({ data: null }))
        ]);
        if (waRes.data) {
          setConfig(waRes.data);
        }
        if (emailRes.data) {
          setEmailConfig(emailRes.data);
        }
        
        // Fetch super admin only settings
        if (isSuperAdmin) {
          const [razorpayRes, bankRes, orgRes] = await Promise.all([
            billingAPI.getRazorpaySettings().catch(() => ({ data: null })),
            billingAPI.getBankSettings().catch(() => ({ data: null })),
            systemSettingsAPI.getEmailDomains().catch(() => ({ data: null }))
          ]);
          if (razorpayRes.data) {
            setRazorpayConfig({
              enabled: razorpayRes.data.enabled || false,
              key_id: razorpayRes.data.key_id || '',
              key_secret: razorpayRes.data.key_secret_masked || '',
              webhook_secret: razorpayRes.data.webhook_secret_masked || '',
              test_mode: razorpayRes.data.test_mode ?? true,
            });
          }
          if (bankRes.data) {
            setBankConfig({
              india: bankRes.data.india || bankConfig.india,
              usa: bankRes.data.usa || bankConfig.usa
            });
          }
          if (orgRes.data) {
            setOrgConfig({
              allowed_domains: orgRes.data.allowed_domains || ['vibe6.com'],
              enable_alerts_for_external: orgRes.data.enable_alerts_for_external || false,
              sync_external_employees: orgRes.data.sync_external_employees || false,
              newDomain: ''
            });
          }
        }
      } catch (error) {
        // Config may not exist yet
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [isSuperAdmin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await whatsappAPI.saveConfig(config);
      toast.success('WhatsApp configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      await timesheetAPI.saveEmailSettings(emailConfig);
      toast.success('Email configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
    } finally {
      setSavingEmail(false);
    }
  };
  
  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await timesheetAPI.testEmail();
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };
  
  const handleSaveRazorpay = async () => {
    setSavingRazorpay(true);
    try {
      await billingAPI.saveRazorpaySettings(razorpayConfig);
      toast.success('Razorpay configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Razorpay configuration');
    } finally {
      setSavingRazorpay(false);
    }
  };
  
  const handleSaveBank = async () => {
    setSavingBank(true);
    try {
      await billingAPI.saveBankSettings(bankConfig);
      toast.success('Bank details saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSavingBank(false);
    }
  };

  // Organization settings handlers
  const handleSaveOrg = async () => {
    setSavingOrg(true);
    try {
      await systemSettingsAPI.updateEmailDomains({
        allowed_domains: orgConfig.allowed_domains,
        enable_alerts_for_external: orgConfig.enable_alerts_for_external,
        sync_external_employees: orgConfig.sync_external_employees
      });
      toast.success('Organization settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save organization settings');
    } finally {
      setSavingOrg(false);
    }
  };

  const addDomain = () => {
    const domain = orgConfig.newDomain.trim().toLowerCase().replace(/^@/, '');
    if (domain && !orgConfig.allowed_domains.includes(domain)) {
      setOrgConfig({
        ...orgConfig,
        allowed_domains: [...orgConfig.allowed_domains, domain],
        newDomain: ''
      });
    }
  };

  const removeDomain = (domain) => {
    if (orgConfig.allowed_domains.length > 1) {
      setOrgConfig({
        ...orgConfig,
        allowed_domains: orgConfig.allowed_domains.filter(d => d !== domain)
      });
    } else {
      toast.error('At least one domain must be configured');
    }
  };

  const handleCleanupPreview = async () => {
    setCleaningUp(true);
    try {
      const res = await systemSettingsAPI.cleanupTestData(true);
      setCleanupResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview cleanup');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleCleanupExecute = async () => {
    if (!window.confirm('Are you sure you want to delete all test data? This action cannot be undone.')) {
      return;
    }
    setCleaningUp(true);
    try {
      const res = await systemSettingsAPI.cleanupTestData(false);
      setCleanupResult(res.data);
      toast.success(res.data.message || 'Test data cleaned up');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup test data');
    } finally {
      setCleaningUp(false);
    }
  };

  // Backup & Restore handlers
  const fetchBackupStatus = async () => {
    setLoadingBackup(true);
    try {
      const res = await systemSettingsAPI.getBackupStatus();
      setBackupStatus(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch backup status');
    } finally {
      setLoadingBackup(false);
    }
  };

  const handleExportBackup = async () => {
    setExporting(true);
    try {
      const res = await systemSettingsAPI.exportBackup();
      const backupData = res.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vibe6_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Backup exported successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export backup');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRestoreFile(file);
    }
  };

  const handleRestore = async (mode) => {
    if (!restoreFile) {
      toast.error('Please select a backup file first');
      return;
    }
    
    const confirmMsg = mode === 'replace' 
      ? 'WARNING: This will DELETE all existing data and replace with backup. Are you absolutely sure?'
      : 'This will merge backup data with existing records. Continue?';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setRestoring(true);
    try {
      const text = await restoreFile.text();
      const backupData = JSON.parse(text);
      
      const res = await systemSettingsAPI.restoreBackup(backupData, mode);
      
      toast.success(`Restore complete (${mode} mode)`);
      setRestoreFile(null);
      fetchBackupStatus(); // Refresh status
      
      // Show summary
      const summary = Object.entries(res.data.collections || {})
        .filter(([_, v]) => v.status === 'success')
        .map(([k, v]) => `${k}: +${v.inserted} /${v.updated} updated`)
        .join('\n');
      if (summary) {
        console.log('Restore summary:', summary);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md text-center">
          <CardContent className="pt-6">
            <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Only administrators can access the settings page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Settings</h1>
        <p className="text-muted-foreground mt-1">Configure integrations and app settings</p>
      </div>

      {/* WhatsApp Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <MessageCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <CardTitle>WhatsApp Cloud API</CardTitle>
              <CardDescription>Configure Meta WhatsApp Business API integration</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <Label className="text-base">Enable WhatsApp Integration</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Allow sending WhatsApp messages from CRM
              </p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
              data-testid="whatsapp-enabled-switch"
            />
          </div>

          <div className="grid gap-4">
            <div className="space-y-2">
              <Label>Phone Number ID</Label>
              <Input
                value={config.phone_number_id}
                onChange={(e) => setConfig({ ...config, phone_number_id: e.target.value })}
                placeholder="Enter your Meta Phone Number ID"
                data-testid="whatsapp-phone-id-input"
              />
              <p className="text-xs text-muted-foreground">
                Found in Meta Business Suite → WhatsApp → API Setup
              </p>
            </div>

            <div className="space-y-2">
              <Label>Business Account ID</Label>
              <Input
                value={config.business_account_id}
                onChange={(e) => setConfig({ ...config, business_account_id: e.target.value })}
                placeholder="Enter your Business Account ID"
                data-testid="whatsapp-business-id-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Access Token</Label>
              <Input
                type="password"
                value={config.access_token}
                onChange={(e) => setConfig({ ...config, access_token: e.target.value })}
                placeholder="Enter your permanent access token"
                data-testid="whatsapp-token-input"
              />
              <p className="text-xs text-muted-foreground">
                Generate a permanent token in Meta Business Settings → System Users
              </p>
            </div>

            <div className="space-y-2">
              <Label>Webhook Verify Token</Label>
              <Input
                value={config.webhook_verify_token}
                onChange={(e) => setConfig({ ...config, webhook_verify_token: e.target.value })}
                placeholder="Enter a custom verify token for webhooks"
                data-testid="whatsapp-verify-token-input"
              />
              <p className="text-xs text-muted-foreground">
                Use this token when setting up the webhook URL in Meta dashboard
              </p>
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              <Input 
                placeholder="Test phone number (e.g., 9876543210)"
                className="w-48"
                id="test-whatsapp-number"
                data-testid="test-whatsapp-number"
              />
              <Button 
                variant="outline" 
                onClick={async () => {
                  const phone = document.getElementById('test-whatsapp-number')?.value;
                  if (!phone) {
                    toast.error('Please enter a phone number');
                    return;
                  }
                  try {
                    const response = await fetch(`${window.location.origin}/api/integrations/whatsapp/notifications/test?phone_number=${phone}&message=Test%20from%20Vibe6%20Settings`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    const data = await response.json();
                    if (data.success) {
                      toast.success(data.mocked ? 'Test message sent (mocked mode)' : 'Test message sent!');
                    } else {
                      toast.error(data.error || 'Failed to send test');
                    }
                  } catch (err) {
                    toast.error('Failed to send test message');
                  }
                }}
                data-testid="test-whatsapp-btn"
              >
                Send Test
              </Button>
            </div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-whatsapp-config-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Azure DevOps Info */}
      <Card>
        <CardHeader>
          <CardTitle>Azure DevOps</CardTitle>
          <CardDescription>Azure DevOps integration configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg bg-muted/50 space-y-2">
            <p className="text-sm">
              <strong>Organization URL:</strong> {process.env.REACT_APP_AZDO_ORG_URL || 'https://dev.azure.com/vibe6digital/'}
            </p>
            <p className="text-sm text-muted-foreground">
              The Azure DevOps PAT (Personal Access Token) is configured in the backend environment variables.
              Contact your system administrator to update the token.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>Email Configuration</CardTitle>
              <CardDescription>Configure SMTP settings for sending timesheet reports</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <Label className="text-base">Enable Email Sending</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Allow sending daily reports and notifications via email
              </p>
            </div>
            <Switch
              checked={emailConfig.enabled}
              onCheckedChange={(v) => setEmailConfig({ ...emailConfig, enabled: v })}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>SMTP Host</Label>
              <Input
                value={emailConfig.smtp_host}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
                placeholder="smtp.gmail.com"
              />
            </div>

            <div className="space-y-2">
              <Label>SMTP Port</Label>
              <Input
                type="number"
                value={emailConfig.smtp_port}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: parseInt(e.target.value) || 587 })}
                placeholder="587"
              />
            </div>

            <div className="space-y-2">
              <Label>SMTP Username</Label>
              <Input
                value={emailConfig.smtp_user}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_user: e.target.value })}
                placeholder="your-email@gmail.com"
              />
            </div>

            <div className="space-y-2">
              <Label>SMTP Password</Label>
              <Input
                type="password"
                value={emailConfig.smtp_password}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
                placeholder="App password or SMTP password"
              />
            </div>

            <div className="space-y-2">
              <Label>From Email</Label>
              <Input
                value={emailConfig.from_email}
                onChange={(e) => setEmailConfig({ ...emailConfig, from_email: e.target.value })}
                placeholder="noreply@vibe6.com"
              />
            </div>

            <div className="space-y-2">
              <Label>From Name</Label>
              <Input
                value={emailConfig.from_name}
                onChange={(e) => setEmailConfig({ ...emailConfig, from_name: e.target.value })}
                placeholder="Vibe6 Intranet"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 rounded-lg border">
            <Switch
              checked={emailConfig.use_tls}
              onCheckedChange={(v) => setEmailConfig({ ...emailConfig, use_tls: v })}
            />
            <div>
              <Label>Use TLS</Label>
              <p className="text-sm text-muted-foreground">Enable TLS encryption for SMTP connection</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail || !emailConfig.enabled}>
              {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Test Email
            </Button>
            <Button onClick={handleSaveEmail} disabled={savingEmail}>
              {savingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Email Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Razorpay Settings (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <CreditCard className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle>Razorpay Payment Gateway</CardTitle>
                <CardDescription>Configure Razorpay for online invoice payments (MOCKED - Enter test keys for testing)</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 rounded-lg border bg-yellow-50 border-yellow-200">
              <div>
                <Label className="text-base">Enable Online Payments</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Allow customers to pay invoices online via Razorpay
                </p>
              </div>
              <Switch
                checked={razorpayConfig.enabled}
                onCheckedChange={(v) => setRazorpayConfig({ ...razorpayConfig, enabled: v })}
              />
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg border">
              <Switch
                checked={razorpayConfig.test_mode}
                onCheckedChange={(v) => setRazorpayConfig({ ...razorpayConfig, test_mode: v })}
              />
              <div>
                <Label>Test Mode</Label>
                <p className="text-sm text-muted-foreground">Use Razorpay test/sandbox environment</p>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Key ID</Label>
                <Input
                  value={razorpayConfig.key_id}
                  onChange={(e) => setRazorpayConfig({ ...razorpayConfig, key_id: e.target.value })}
                  placeholder="rzp_test_xxxxxxxxxxxx"
                />
                <p className="text-xs text-muted-foreground">
                  Get your Key ID from Razorpay Dashboard → Settings → API Keys
                </p>
              </div>

              <div className="space-y-2">
                <Label>Key Secret</Label>
                <Input
                  type="password"
                  value={razorpayConfig.key_secret}
                  onChange={(e) => setRazorpayConfig({ ...razorpayConfig, key_secret: e.target.value })}
                  placeholder="Enter new secret or leave blank to keep existing"
                />
              </div>

              <div className="space-y-2">
                <Label>Webhook Secret (Optional)</Label>
                <Input
                  type="password"
                  value={razorpayConfig.webhook_secret}
                  onChange={(e) => setRazorpayConfig({ ...razorpayConfig, webhook_secret: e.target.value })}
                  placeholder="Enter webhook secret for payment verification"
                />
                <p className="text-xs text-muted-foreground">
                  Configure webhook URL in Razorpay: POST to /api/billing/payments/razorpay/webhook
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveRazorpay} disabled={savingRazorpay}>
                {savingRazorpay ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Razorpay Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bank Details Settings (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Building2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle>Company Bank Details</CardTitle>
                <CardDescription>Bank details shown on invoices and payment pages</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* India Bank Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                🇮🇳 India (VIBE6 DIGITAL LLP)
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={bankConfig.india.bank_name}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, bank_name: e.target.value } })}
                    placeholder="IDFC FIRST Bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Input
                    value={bankConfig.india.branch}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, branch: e.target.value } })}
                    placeholder="INDORE – VIJAY NAGAR BRANCH"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={bankConfig.india.account_number}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, account_number: e.target.value } })}
                    placeholder="10219885980"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IFSC Code</Label>
                  <Input
                    value={bankConfig.india.ifsc}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, ifsc: e.target.value } })}
                    placeholder="IDFB0041269"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SWIFT Code</Label>
                  <Input
                    value={bankConfig.india.swift_code}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, swift_code: e.target.value } })}
                    placeholder="IDFBINBBMUM"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UPI ID</Label>
                  <Input
                    value={bankConfig.india.upi_id}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, upi_id: e.target.value } })}
                    placeholder="vibes6digitalllp@idfcbank"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GSTIN</Label>
                  <Input
                    value={bankConfig.india.gstin || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, gstin: e.target.value } })}
                    placeholder="23AABFV1234C1Z5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    value={bankConfig.india.contact_email || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, contact_email: e.target.value } })}
                    placeholder="accounts@vibe6.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={bankConfig.india.contact_phone || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, contact_phone: e.target.value } })}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Registered Address</Label>
                  <Input
                    value={bankConfig.india.address || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, india: { ...bankConfig.india, address: e.target.value } })}
                    placeholder="123, Business Park, Indore, MP 452010"
                  />
                </div>
              </div>
            </div>

            {/* USA Bank Details */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                🇺🇸 USA (VIBE6 DIGITAL LLC)
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={bankConfig.usa.company_name || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, usa: { ...bankConfig.usa, company_name: e.target.value } })}
                    placeholder="VIBE6 DIGITAL LLC"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={bankConfig.usa.bank_name}
                    onChange={(e) => setBankConfig({ ...bankConfig, usa: { ...bankConfig.usa, bank_name: e.target.value } })}
                    placeholder="Chase Bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Zelle Email</Label>
                  <Input
                    value={bankConfig.usa.zelle_email}
                    onChange={(e) => setBankConfig({ ...bankConfig, usa: { ...bankConfig.usa, zelle_email: e.target.value } })}
                    placeholder="kapil.jain@vibe6.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>EIN (Tax ID)</Label>
                  <Input
                    value={bankConfig.usa.ein || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, usa: { ...bankConfig.usa, ein: e.target.value } })}
                    placeholder="12-3456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    value={bankConfig.usa.contact_email || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, usa: { ...bankConfig.usa, contact_email: e.target.value } })}
                    placeholder="accounts-us@vibe6.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={bankConfig.usa.contact_phone || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, usa: { ...bankConfig.usa, contact_phone: e.target.value } })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Registered Address</Label>
                  <Input
                    value={bankConfig.usa.address || ''}
                    onChange={(e) => setBankConfig({ ...bankConfig, usa: { ...bankConfig.usa, address: e.target.value } })}
                    placeholder="123 Business Ave, Suite 100, City, State 12345"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveBank} disabled={savingBank}>
                {savingBank ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Bank Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organization Settings (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Globe className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle>Organization Settings</CardTitle>
                <CardDescription>Configure email domains and data sync settings</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Allowed Email Domains */}
            <div className="space-y-4">
              <div>
                <Label className="text-base">Allowed Email Domains</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Only employees with these email domains will receive system alerts and notifications
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {orgConfig.allowed_domains.map((domain) => (
                  <Badge key={domain} variant="secondary" className="px-3 py-1.5 text-sm">
                    @{domain}
                    {orgConfig.allowed_domains.length > 1 && (
                      <button
                        onClick={() => removeDomain(domain)}
                        className="ml-2 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={orgConfig.newDomain}
                  onChange={(e) => setOrgConfig({ ...orgConfig, newDomain: e.target.value })}
                  placeholder="Enter domain (e.g., company.com)"
                  onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                  className="max-w-xs"
                />
                <Button variant="outline" onClick={addDomain}>
                  <Plus className="w-4 h-4 mr-1" /> Add Domain
                </Button>
              </div>
            </div>

            {/* External Alerts Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <Label className="text-base">Send Alerts to External Domains</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Allow timesheet reminders and system alerts for employees outside whitelisted domains
                </p>
              </div>
              <Switch
                checked={orgConfig.enable_alerts_for_external}
                onCheckedChange={(v) => setOrgConfig({ ...orgConfig, enable_alerts_for_external: v })}
              />
            </div>

            {/* Sync External Employees Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <Label className="text-base">Sync External Employees</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Include employees from external domains during AD sync operations
                </p>
              </div>
              <Switch
                checked={orgConfig.sync_external_employees}
                onCheckedChange={(v) => setOrgConfig({ ...orgConfig, sync_external_employees: v })}
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveOrg} disabled={savingOrg}>
                {savingOrg ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Organization Settings
              </Button>
            </div>

            {/* Test Data Cleanup */}
            <div className="pt-6 border-t">
              <div className="flex items-center gap-2 mb-4">
                <Trash2 className="w-5 h-5 text-destructive" />
                <h3 className="font-semibold text-lg">Clean Up Test Data</h3>
              </div>
              <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-800 mb-4">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  This will permanently delete all employees and users with email domains not in your allowed list. This action cannot be undone.
                </AlertDescription>
              </Alert>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleCleanupPreview} disabled={cleaningUp}>
                  {cleaningUp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Preview Cleanup
                </Button>
                {cleanupResult && cleanupResult.dry_run && (cleanupResult.test_employees_found > 0 || cleanupResult.test_users_found > 0) && (
                  <Button variant="destructive" onClick={handleCleanupExecute} disabled={cleaningUp}>
                    {cleaningUp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Delete {cleanupResult.test_employees_found + cleanupResult.test_users_found} Records
                  </Button>
                )}
              </div>

              {cleanupResult && (
                <div className="mt-4 p-4 rounded-lg border bg-muted/50">
                  <p className="font-medium mb-2">
                    {cleanupResult.dry_run ? 'Preview Results:' : 'Cleanup Complete:'}
                  </p>
                  <ul className="text-sm space-y-1">
                    <li>Allowed domains: {cleanupResult.allowed_domains?.join(', ')}</li>
                    <li>Test employees found: {cleanupResult.test_employees_found}</li>
                    <li>Test users found: {cleanupResult.test_users_found}</li>
                    {!cleanupResult.dry_run && (
                      <>
                        <li className="text-green-700">Deleted employees: {cleanupResult.deleted_employees}</li>
                        <li className="text-green-700">Deleted users: {cleanupResult.deleted_users}</li>
                      </>
                    )}
                  </ul>
                  {cleanupResult.test_employees?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium mb-1">Test Employees to Remove:</p>
                      <div className="max-h-32 overflow-y-auto text-xs bg-white rounded p-2">
                        {cleanupResult.test_employees.map((emp, i) => (
                          <div key={i}>{emp.name} - {emp.email}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Digest (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-100">
                <Mail className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <CardTitle>Daily Digest</CardTitle>
                <CardDescription>Preview and send daily summary emails to managers and admins</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-sm text-muted-foreground">
              <p>The daily digest includes:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Pending leave approvals</li>
                <li>Pending expense claims</li>
                <li>Pending timesheet approvals</li>
                <li>Overdue CRM follow-ups</li>
                <li>Aging leads alerts</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={async () => {
                  setLoadingDigest(true);
                  try {
                    const res = await systemSettingsAPI.previewDigest();
                    setDigestPreview(res.data);
                    toast.success('Digest preview loaded');
                  } catch (error) {
                    toast.error('Failed to load preview');
                  } finally {
                    setLoadingDigest(false);
                  }
                }} 
                disabled={loadingDigest}
              >
                {loadingDigest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Preview My Digest
              </Button>
              <Button 
                variant="outline"
                onClick={async () => {
                  setSendingDigest(true);
                  try {
                    await systemSettingsAPI.sendDigestToMe();
                    toast.success('Digest email sent to your inbox');
                  } catch (error) {
                    toast.error('Failed to send digest email');
                  } finally {
                    setSendingDigest(false);
                  }
                }} 
                disabled={sendingDigest}
              >
                {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send to Me
              </Button>
              <Button 
                onClick={async () => {
                  if (!window.confirm('Send daily digest to ALL managers and admins?')) return;
                  setSendingDigest(true);
                  try {
                    const res = await systemSettingsAPI.sendDigest();
                    toast.success(`Digest sent to ${res.data.sent} recipients`);
                  } catch (error) {
                    toast.error('Failed to send digest');
                  } finally {
                    setSendingDigest(false);
                  }
                }} 
                disabled={sendingDigest}
              >
                {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send to All
              </Button>
            </div>

            {digestPreview && (
              <div className="mt-4 border rounded-lg overflow-hidden">
                <div className="p-3 bg-muted border-b">
                  <p className="font-medium">Digest Preview for {digestPreview.digest_data?.user_name}</p>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  {Object.entries(digestPreview.digest_data?.sections || {}).map(([key, value]) => {
                    if (!value || (Array.isArray(value) && value.length === 0)) return null;
                    const count = Array.isArray(value) ? value.length : 
                                  (typeof value === 'object' ? Object.values(value).flat().length : 0);
                    if (count === 0) return null;
                    return (
                      <div key={key} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    );
                  })}
                  {Object.keys(digestPreview.digest_data?.sections || {}).every(key => {
                    const val = digestPreview.digest_data?.sections[key];
                    return !val || (Array.isArray(val) && val.length === 0) || 
                           (typeof val === 'object' && Object.values(val).flat().length === 0);
                  }) && (
                    <p className="text-center text-green-600 py-2">All caught up! No pending items.</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Document Expiry Alerts (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle>Document Expiry Alerts</CardTitle>
                <CardDescription>Monitor employee document expiry and send HR alerts</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-sm text-muted-foreground">
              <p>Automatically monitors employee documents with expiry dates:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Passport, Visa, Work Permit (Critical)</li>
                <li>Driving License, Health Insurance</li>
                <li>Professional Certifications, Compliance Documents</li>
              </ul>
              <p className="mt-2">Daily alerts are sent to HR at 9:15 AM IST.</p>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={async () => {
                  setLoadingExpiry(true);
                  try {
                    const res = await systemSettingsAPI.getDocumentExpirySummary();
                    setExpiryData(res.data);
                    toast.success('Expiry data loaded');
                  } catch (error) {
                    toast.error('Failed to load expiry data');
                  } finally {
                    setLoadingExpiry(false);
                  }
                }} 
                disabled={loadingExpiry}
              >
                {loadingExpiry ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Check Expiry Status
              </Button>
              <Button 
                variant="default"
                onClick={async () => {
                  if (!window.confirm('Send document expiry alert to HR?')) return;
                  setSendingExpiryAlert(true);
                  try {
                    const res = await systemSettingsAPI.sendDocumentExpiryAlert();
                    if (res.data.alerts_sent > 0) {
                      toast.success(`Alert sent to ${res.data.alerts_sent} HR personnel`);
                    } else {
                      toast.info(res.data.message || 'No documents expiring');
                    }
                  } catch (error) {
                    toast.error('Failed to send alert');
                  } finally {
                    setSendingExpiryAlert(false);
                  }
                }} 
                disabled={sendingExpiryAlert}
              >
                {sendingExpiryAlert ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Alert to HR
              </Button>
              <Button 
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Send expiry reminders to all employees with expiring documents?')) return;
                  setSendingExpiryAlert(true);
                  try {
                    const res = await systemSettingsAPI.notifyEmployees();
                    if (res.data.employees_notified > 0) {
                      toast.success(`Reminders sent to ${res.data.employees_notified} employees`);
                    } else {
                      toast.info(res.data.message || 'No employees to notify');
                    }
                  } catch (error) {
                    toast.error('Failed to send employee reminders');
                  } finally {
                    setSendingExpiryAlert(false);
                  }
                }} 
                disabled={sendingExpiryAlert}
              >
                {sendingExpiryAlert ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                Notify Employees
              </Button>
            </div>

            {expiryData && (
              <div className="mt-4 space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <div className="text-2xl font-bold text-red-600">{expiryData.total_expired || 0}</div>
                    <div className="text-sm text-red-700">Expired</div>
                  </div>
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="text-2xl font-bold text-orange-600">{expiryData.urgent_7_days || 0}</div>
                    <div className="text-sm text-orange-700">Expiring in 7 days</div>
                  </div>
                  <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                    <div className="text-2xl font-bold text-yellow-600">{expiryData.warning_14_days || 0}</div>
                    <div className="text-sm text-yellow-700">Expiring in 14 days</div>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{expiryData.notice_30_days || 0}</div>
                    <div className="text-sm text-blue-700">Expiring in 30 days</div>
                  </div>
                </div>

                {/* Critical Alert */}
                {expiryData.critical_count > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Critical Documents Attention Required</AlertTitle>
                    <AlertDescription>
                      {expiryData.critical_count} critical document(s) (Passport, Visa, Work Permit) need immediate attention.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Expired Documents */}
                {expiryData.expired_documents && expiryData.expired_documents.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-3 bg-red-100 border-b font-medium text-red-800">
                      Expired Documents
                    </div>
                    <div className="divide-y">
                      {expiryData.expired_documents.slice(0, 5).map((doc, idx) => (
                        <div key={idx} className="p-3 flex justify-between items-center">
                          <div>
                            <div className="font-medium">{doc.employee_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {doc.document_type.replace(/_/g, ' ')} • Expired {doc.days_expired} days ago
                            </div>
                          </div>
                          <Badge variant="destructive">{doc.expiry_date?.slice(0, 10)}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expiring Soon */}
                {expiryData.expiring_soon && expiryData.expiring_soon.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-3 bg-orange-100 border-b font-medium text-orange-800">
                      Expiring Within 7 Days
                    </div>
                    <div className="divide-y">
                      {expiryData.expiring_soon.slice(0, 5).map((doc, idx) => (
                        <div key={idx} className="p-3 flex justify-between items-center">
                          <div>
                            <div className="font-medium">{doc.employee_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {doc.document_type.replace(/_/g, ' ')} • {doc.days_until_expiry} days left
                            </div>
                          </div>
                          <Badge variant="secondary">{doc.expiry_date?.slice(0, 10)}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Clear */}
                {expiryData.total_expired === 0 && expiryData.total_expiring_30_days === 0 && (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-green-600 font-medium">All Clear!</p>
                    <p className="text-sm text-muted-foreground">No documents expiring in the next 30 days.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Asset Allocation Reminders (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle>Asset Allocation Reminders</CardTitle>
                <CardDescription>Follow-up reminders for pending asset acknowledgments</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-sm text-muted-foreground">
              <p>Automatic reminders sent to employees who haven't responded to asset allocations:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Day 3:</strong> First reminder email</li>
                <li><strong>Day 5:</strong> Second reminder (CC: Manager + Super Admin)</li>
                <li><strong>Day 7+:</strong> Urgent reminder (CC: Manager + Super Admin)</li>
              </ul>
              <p className="mt-2">Scheduled to run daily at 10:00 AM IST.</p>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={async () => {
                  setLoadingAllocation(true);
                  try {
                    const res = await assetAPI.getAllocationReminderSummary();
                    setAllocationData(res.data);
                    toast.success('Allocation data loaded');
                  } catch (error) {
                    toast.error('Failed to load allocation data');
                  } finally {
                    setLoadingAllocation(false);
                  }
                }} 
                disabled={loadingAllocation}
              >
                {loadingAllocation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Check Status
              </Button>
              <Button 
                variant="default"
                onClick={async () => {
                  if (!window.confirm('Send follow-up reminders to employees with pending asset allocations?')) return;
                  setSendingAllocationReminders(true);
                  try {
                    const res = await assetAPI.sendAllocationReminders();
                    if (res.data.reminders_sent > 0) {
                      toast.success(`${res.data.reminders_sent} reminders sent`);
                    } else {
                      toast.info(res.data.message || 'No pending allocations to remind');
                    }
                  } catch (error) {
                    toast.error('Failed to send reminders');
                  } finally {
                    setSendingAllocationReminders(false);
                  }
                }} 
                disabled={sendingAllocationReminders}
              >
                {sendingAllocationReminders ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Reminders
              </Button>
            </div>

            {allocationData && (
              <div className="mt-4 space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{allocationData.total_pending || 0}</div>
                    <div className="text-sm text-blue-700">Total Pending</div>
                  </div>
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="text-2xl font-bold text-orange-600">{allocationData.pending_3_plus_days || 0}</div>
                    <div className="text-sm text-orange-700">3+ Days Pending</div>
                  </div>
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <div className="text-2xl font-bold text-red-600">{allocationData.pending_7_plus_days || 0}</div>
                    <div className="text-sm text-red-700">7+ Days (Urgent)</div>
                  </div>
                </div>

                {/* Pending Allocations List */}
                {allocationData.oldest_pending && allocationData.oldest_pending.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-3 bg-purple-100 border-b font-medium text-purple-800">
                      Oldest Pending Allocations
                    </div>
                    <div className="divide-y">
                      {allocationData.oldest_pending.slice(0, 5).map((alloc, idx) => (
                        <div key={idx} className="p-3 flex justify-between items-center">
                          <div>
                            <div className="font-medium">{alloc.employee_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {alloc.asset_ids?.length || 1} asset(s) • {alloc.days_pending} days pending
                            </div>
                          </div>
                          <Badge variant={alloc.days_pending >= 7 ? "destructive" : alloc.days_pending >= 3 ? "secondary" : "outline"}>
                            {alloc.days_pending} days
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Clear */}
                {allocationData.total_pending === 0 && (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                    <p className="text-green-600 font-medium">All Clear!</p>
                    <p className="text-sm text-muted-foreground">No pending asset allocations</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Backup & Restore (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Database className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle>Backup & Restore</CardTitle>
                <CardDescription>Export and restore database collections</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Database Status */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Database Status</h3>
                <Button variant="outline" size="sm" onClick={fetchBackupStatus} disabled={loadingBackup}>
                  {loadingBackup ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Refresh
                </Button>
              </div>
              
              {backupStatus ? (
                <div className="p-4 rounded-lg border bg-muted/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Database</p>
                      <p className="font-medium">{backupStatus.database}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Records</p>
                      <p className="font-medium">{backupStatus.total_records?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Collections</p>
                      <p className="font-medium">{Object.keys(backupStatus.collections || {}).length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Last Check</p>
                      <p className="font-medium">{new Date(backupStatus.timestamp).toLocaleTimeString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    {Object.entries(backupStatus.collections || {}).map(([name, count]) => (
                      <div key={name} className="flex justify-between px-2 py-1 bg-white rounded">
                        <span className="text-muted-foreground">{name}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Click "Refresh" to load database status</p>
                </div>
              )}
            </div>

            {/* Export Backup */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-4">
                <Download className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-lg">Export Backup</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Download a complete backup of all critical database collections as a JSON file.
              </p>
              <Button onClick={handleExportBackup} disabled={exporting}>
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Export Full Backup
              </Button>
            </div>

            {/* Restore Backup */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-4">
                <Upload className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-lg">Restore Backup</h3>
              </div>
              
              <Alert className="bg-amber-50 border-amber-200 text-amber-800 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  <strong>Caution:</strong> Restoring a backup can modify or delete existing data. Always export a backup before restoring.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="backup-file">Select Backup File</Label>
                  <Input
                    id="backup-file"
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="mt-2"
                  />
                  {restoreFile && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Selected: {restoreFile.name} ({(restoreFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => handleRestore('merge')} 
                    disabled={!restoreFile || restoring}
                  >
                    {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    Merge Restore
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => handleRestore('replace')} 
                    disabled={!restoreFile || restoring}
                  >
                    {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Full Replace
                  </Button>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p><strong>Merge:</strong> Adds new records and updates existing ones (safe)</p>
                  <p><strong>Full Replace:</strong> Deletes all data and replaces with backup (destructive)</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
